// @ts-check
import React from 'react';
import './Button.css';

/**
 * @typedef {'primary' | 'danger' | 'ghost'} ButtonVariant
 * @typedef {'sm' | 'md'} ButtonSize
 */

/**
 * Button — primary admin-action surface.
 *
 * Variants:
 *   - primary  → main affirmative action (Submit, Sign in, Confirm)
 *   - danger   → destructive action (Delete, Disable, Purge)
 *   - ghost    → secondary / cancel / dismiss
 *
 * Sizes:
 *   - md (default) → form/page-level actions
 *   - sm           → table-row actions; tighter padding to keep row density
 *
 * Loading state shows an inline spinner and forces aria-busy + disabled.
 *
 * @param {Object} props
 * @param {ButtonVariant} [props.variant]
 * @param {ButtonSize} [props.size]
 * @param {boolean} [props.loading]
 * @param {boolean} [props.disabled]
 * @param {'button' | 'submit' | 'reset'} [props.type]
 * @param {React.ReactNode} props.children
 * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} [props.onClick]
 * @param {string} [props.className]
 * @param {string} [props.title]
 * @param {string} [props.aria-label]
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  type = 'button',
  children,
  onClick,
  className = '',
  ...rest
}) {
  const isDisabled = disabled || loading;
  const classes = ['btn', `btn--${variant}`, `btn--${size}`, loading ? 'btn--loading' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      onClick={onClick}
      {...rest}
    >
      {loading && <span className="btn__spinner" aria-hidden="true" />}
      <span className="btn__label">{children}</span>
    </button>
  );
}
